import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Box, Chip, Typography, Divider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: '500px',
  },
  divider: {
    margin: '20px 0',
  },
  time: {
    display: 'inline-block',
    marginLeft: '5px',
    color: theme.palette.error.main,
  },
  chip: {
    marginRight: '5px',
    marginBottom: '5px',
  },
  infoLabel: {
    display: 'inline-block',
    marginBottom: '2px',
  },
}));

const TrainingSessionView = ({ session }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const {
    startTime,
    endTime,
    startDate,
    endDate,
    maxStudents,
    description,
    trainerName,
    trainingModuleName,
    trainingModuleCode,
    trainingPartCode,
    location,
    deliveryMethod,
    status,
    stationName,
    participantCount,
  } = session;
  const displayName = [trainingModuleName || trainingModuleCode, trainingPartCode].filter(Boolean).join(' · ');
  const isElearning = deliveryMethod === 'elearning';

  return (
    <div className={classes.root}>
      <div className={classes.headSection}>
        <Grid container justify="space-between">
          {isElearning ? (
            <>
              <Grid container item xs={5} direction="column">
                <Grid item>
                  <Box mb={2}>
                    {t('trainingSessions.deliveryMethod')}
                    {':'}
                    <span className={classes.time}>{t(`trainingSessions.deliveryMethods.${deliveryMethod}`)}</span>
                  </Box>
                </Grid>
              </Grid>
              <Grid container item xs={5} direction="column">
                <Grid item>
                  <Box mb={2}>
                    {t('formLabels.status')}
                    {':'}
                    <span className={classes.time}>{t(`trainingSessions.statuses.${status}`)}</span>
                  </Box>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid container item xs={5} direction="column">
                <Grid item>
                  <Box mb={2}>
                    {t('schedule.startDate')}
                    {':'}
                    <span className={classes.time}>{startDate ? moment(startDate).format('DD.MM.YYYY') : '-'}</span>
                  </Box>
                </Grid>
                <Grid item>
                  <Box mb={2}>
                    {t('schedule.endDate')}
                    {':'}
                    <span className={classes.time}>{endDate ? moment(endDate).format('DD.MM.YYYY') : '-'}</span>
                  </Box>
                </Grid>
              </Grid>
              <Grid container item xs={5} direction="column">
                <Grid item>
                  <Box mb={2}>
                    {t('schedule.startingTime')}
                    {':'}
                    <span className={classes.time}>{startTime || '-'}</span>
                  </Box>
                </Grid>
                <Grid item>
                  <Box mb={2}>
                    {t('schedule.endingTime')}
                    {':'}
                    <span className={classes.time}>{endTime || '-'}</span>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </div>
      <Divider className={classes.divider} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">{displayName}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Chip
            className={classes.chip}
            label={t(`trainingSessions.deliveryMethods.${deliveryMethod}`)}
            color="primary"
            size="small"
          />
          <Chip
            className={classes.chip}
            label={t(`trainingSessions.statuses.${status}`)}
            variant="outlined"
            size="small"
          />
          {stationName && <Chip className={classes.chip} label={stationName} variant="outlined" size="small" />}
        </Grid>
        {location && (
          <Grid item xs={12}>
            <span className={classes.infoLabel}>{t('schedule.location')}</span>
            {': '}
            {location}
          </Grid>
        )}
        {trainerName && (
          <Grid item xs={12}>
            <span className={classes.infoLabel}>{t('schedule.trainers')}</span>
            {': '}
            {trainerName}
          </Grid>
        )}
        {maxStudents != null && (
          <Grid item xs={12}>
            <span className={classes.infoLabel}>{t('schedule.maxStudents')}</span>
            {': '}
            {maxStudents}
          </Grid>
        )}
        {participantCount != null && (
          <Grid item xs={12}>
            <span className={classes.infoLabel}>{t('trainingSessions.participants')}</span>
            {': '}
            {participantCount}
          </Grid>
        )}
        {description && (
          <Grid item xs={12}>
            <span className={classes.infoLabel}>{t('schedule.description')}</span>
            {': '}
            {description}
          </Grid>
        )}
      </Grid>
    </div>
  );
};

TrainingSessionView.propTypes = {
  session: PropTypes.shape({}).isRequired,
};

export default TrainingSessionView;
